import { Timer } from 'three/addons/misc/Timer.js';
import * as THREE from 'three';
import * as THREE_WEBGPU from 'three/webgpu';
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
import { FontLoader } from 'three/addons/loaders/FontLoader.js';
// OPTIMIZATION START - Quality profile support
import { getQualityProfile } from '../config/QualityProfiles.js';
// OPTIMIZATION END
// Analytics removed - GameArea.js handles all tracking now
// import analyticsService from '../services/AnalyticsService';
// import { getFormScores } from '../Exercise/ExerciseDetection.js';

const use_webgpu = false;
window.use_webgpu = use_webgpu; // Add this line to set the global flag

// BaseGame.js
export class BaseGame {
    constructor(on_init = [], on_animate = []) {
        this.scene_data;
        this.timer = new Timer();
        this.elapsedTime = 0;
        this.on_init = on_init;
        this.on_animate = on_animate;
        this.fontLoader = new FontLoader();
        this.streak = 0;
        this.floating_text = [];
        this.font = null;
        //this.videoplane = null
        this.prev_camera_position = new THREE.Vector3()
        this.camera_distance = 0.75
        this.camera_scale = 1
        this.camera_offset = new THREE.Vector3(0, 0, 0)
        // Reusable vectors to avoid garbage collection
        this._forward = new THREE.Vector3()
        this._pos = new THREE.Vector3()
        this.animate = this.animate.bind(this)
        this.camera_canvas = document.getElementById("camera_canvas")
        this.canvas_scale = '-1 1'
        this.canvas_offset = 'translate(0%, 0%)'
        this.use_webgpu = use_webgpu;
        
        // ElevenLabs TTS for encouragement
        this.currentReps = 0;
        this.targetReps = 30; // Default target
        this.gameType = 'game'; // Will be set by each game
        this.encouragementMilestones = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

        // Analytics tracking
        this.gameStartTime = null;
        this.gameName = 'Unknown Game'; // Should be set by each game
        this.hasStartedTracking = false; // Track if game has started (unpaused)
        this.hasTrackedEnd = false; // Prevent duplicate end tracking
    }

    animate(){
        this.animationId = requestAnimationFrame(this.animate);
        this.timer.update();
        
        if(!this.game_state || this.game_state.paused){
            return
        }

        var time_delta = this.timer.getDelta();
        this.on_animate.forEach(func => func(time_delta))

        this.updateScene(time_delta)
        this.update_text(time_delta)
    }

    // Cleanup method to stop animation loop
    cleanup() {
        
        // Analytics tracking removed - handled by GameArea.js now

        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        // CRITICAL: Stop ALL ElevenLabs TTS audio (HTML5 Audio + browser TTS)
        if (window.elevenLabsTTS) {
            window.elevenLabsTTS.stopAllAudio();
            window.elevenLabsTTS.stopInactivityMonitoring();
        }

        // CRITICAL: Stop all Howler sounds to prevent audio continuing after exit
        if (typeof Howler !== 'undefined') {
            Howler.stop();
        }

        // DON'T clear on_animate callbacks - they need to persist for game restart
        // The callbacks are set once in constructor and should remain across sessions
        // this.on_animate = [];

        // Dispose of Three.js resources if they exist
        if (this.scene_data) {
            if (this.scene_data.renderer) {
                this.scene_data.renderer.dispose();
            }
            this.scene_data = null;
        }
    }
/* OLD camera plane code
    update_camera_plane(){
        if (this.videoplane && this.videoplane.material.map) {
            this.videoplane.material.map.needsUpdate = true;
        }
        this.prev_camera_position.copy(this.scene_data.camera.position)
        
        // Reuse existing vectors
        this.scene_data.camera.getWorldDirection(this._forward)
        this._pos.copy(this.scene_data.camera.position)
            .add(this._forward.multiplyScalar(this.camera_distance))
        
        //this.videoplane.lookAt(this.scene_data.camera.position)
        this.videoplane.position.y += 0.5
        this.videoplane.position.copy(this._pos)
        
    }

    set_camera_distance(distance){
        const forward = new THREE.Vector3();
        this.scene_data.camera.getWorldDirection(forward)
        this.camera_distance = distance
        const pos = new THREE.Vector3().copy(this.scene_data.camera.position)
                .add(forward.multiplyScalar(this.camera_distance))
            
        this.videoplane.lookAt(this.scene_data.camera.position)
        this.videoplane.position.y += 0.5
        this.videoplane.position.set(pos.x, pos.y, pos.z);

    }

    move_camera_to_front(){
        this.scene_data.background_scene.remove(this.videoplane)
        this.scene_data.scene.add(this.videoplane)
    }
        */

    // OPTIMIZATION: WebGL context loss/restore handlers for iPad/iOS stability
    handleContextLoss() {
        // Stop animation loop
        if (this.timer) {
            this.timer.stop();
        }
        // Display message to user
        const errorMsg = document.createElement('div');
        errorMsg.id = 'context-loss-message';
        errorMsg.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0,0,0,0.8);
            color: white;
            padding: 20px;
            border-radius: 10px;
            z-index: 10000;
            text-align: center;
        `;
        errorMsg.innerHTML = `
            <h3>Graphics Context Lost</h3>
            <p>The game's graphics have been interrupted.</p>
            <p>Attempting to recover...</p>
        `;
        document.body.appendChild(errorMsg);
    }

    handleContextRestore() {
        // Remove error message
        const errorMsg = document.getElementById('context-loss-message');
        if (errorMsg) {
            errorMsg.remove();
        }
        // Restart animation loop
        if (this.timer) {
            this.timer.start();
        }
        // Recreate scene resources if needed
        
    }

    updateScene(time_delta){
        this.elapsedTime += time_delta;

        // Render both scenes - background first, then foreground
        if (this.scene_data.renderer) {
            this.scene_data.renderer.clear();
            this.scene_data.renderer.render(this.scene_data.background_scene, this.scene_data.camera);
            
            // Render foreground scene on top without clearing
            this.scene_data.renderer.autoClear = false;
            this.scene_data.renderer.render(this.scene_data.scene, this.scene_data.camera);
            this.scene_data.renderer.autoClear = true;
        }
    }

    onWindowResize(window) {
        // Guard: Check if scene_data is initialized
        if (!this.scene_data || !this.scene_data.camera) {
            console.warn('⚠️ onWindowResize called before scene_data initialized');
            return;
        }

        // OPTIMIZATION: Use quality profile for adaptive pixel ratio
        const quality = getQualityProfile?.() || { rendering: { pixelRatio: 1 } };

        this.scene_data.camera.aspect = window.innerWidth / window.innerHeight;
        this.scene_data.camera.updateProjectionMatrix();

        // Single renderer with adaptive pixel ratio
        const pixelRatio = Math.min(window.devicePixelRatio * quality.rendering.pixelRatio, 2);
        this.scene_data.renderer.setSize(window.innerWidth, window.innerHeight);
        this.scene_data.renderer.setPixelRatio(pixelRatio);
    }

    create_lights(scene){
        // OPTIMIZATION: Use quality profile for conditional shadows
        const quality = getQualityProfile?.() || {
            rendering: {
                disableShadows: false,
                shadowMapSize: 1024,
                shadowDistance: 40
            }
        };

        // Ambient light (always enabled)
        const light = new THREE.AmbientLight(new THREE.Color(1, 1, 1), 1.0);
        scene.add(light);

        // Conditional shadow-casting lights based on quality
        if (!quality.rendering.disableShadows) {
            const light2 = new THREE.DirectionalLight(new THREE.Color(1, 1, 1), 2.0);
            light2.position.set(-5, 4, 2);
            light2.castShadow = true;

            // Configure shadow camera frustum
            light2.shadow.camera.left = -15;
            light2.shadow.camera.right = 15;
            light2.shadow.camera.top = 15;
            light2.shadow.camera.bottom = -15;
            light2.shadow.camera.near = 0.1;
            light2.shadow.camera.far = quality.rendering.shadowDistance || 40;

            // Adaptive shadow resolution
            light2.shadow.mapSize.width = quality.rendering.shadowMapSize;
            light2.shadow.mapSize.height = quality.rendering.shadowMapSize;
            light2.target = new THREE.Object3D();
            light2.target.position.set(0, 0, 0);
            light2.shadow.normalBias = 0.05;
            scene.add(light2.target);
            scene.add(light2);

            const light3 = new THREE.DirectionalLight(new THREE.Color(1, 1, 1), 1.0);
            light3.position.set(-2, 1, -1);
            light3.castShadow = true;

            // Configure shadow properties with adaptive resolution
            light3.shadow.mapSize.width = quality.rendering.shadowMapSize;
            light3.shadow.mapSize.height = quality.rendering.shadowMapSize;
            light3.shadow.camera.near = 0.1;
            light3.shadow.camera.far = Math.min(20, quality.rendering.shadowDistance || 20);
            light3.shadow.normalBias = 0.05;
            scene.add(light3);
        }
    }

    show_text(text, position){
        // Load font and create text
        if(!this.font){
            this.fontLoader.load('https://threejs.org/examples/fonts/helvetiker_regular.typeface.json', (loadedFont) => {
                this.font = loadedFont;
                this.show_text(text, position);
            });
        }
        else{
            const geometry = new TextGeometry(text.toString(), {
                font: this.font,
                size: 1.6,
                depth: 0.2,
                curveSegments: 12,
                bevelEnabled: true,
                bevelThickness: 0.02,
                bevelSize: 0.02,
                bevelSegments: 5,
            });

            let color = new THREE.Color(Math.random(), Math.random(), Math.random())

            const material = new THREE.MeshStandardMaterial({ 
                color: color,
                emissive: color,
                emissiveIntensity: 0.5
            });
            
            let obj = new THREE.Mesh(geometry, material);
            geometry.center(); // Center text
            obj.scale.set(0.5,0.5,0.5)
            obj.position.set(position.x, position.y, position.z);
            this.floating_text.push(obj);
            this.scene_data.scene.add(obj);
        }
    }

    update_text(time_delta){
        this.floating_text.forEach(obj => {
            obj.position.y += time_delta*2;
            if(obj.position.y > 10){
                this.scene_data.scene.remove(obj);
                this.floating_text.splice(this.floating_text.indexOf(obj), 1);
            }
        });
    }

    increment_streak(position){
        this.streak++;
        this.show_text(this.streak, position);
    }

    clear_streak(position){
        if(this.streak > 0){
            this.streak = 0;
            this.show_text(this.streak, position);
        }
    }

    set_canvas_scale(scale){
        this.canvas_scale = scale
        document.getElementById("camera_canvas").style.scale = this.canvas_scale
    }

    set_canvas_offset(offset){
        this.canvas_offset = offset
        document.getElementById("camera_canvas").style.transform = this.canvas_offset
    }
    
    // Method to update reps and trigger encouragement
    updateReps(newReps) {
        this.currentReps = newReps;

        // Update activity for inactivity monitoring
        if (window.elevenLabsTTS) {
            window.elevenLabsTTS.updateActivity(newReps, this.gameType);
        }

        // Track game completion when target is reached
        if (this.currentReps >= this.targetReps && this.targetReps > 0 && !this.hasTrackedEnd) {
            this.hasTrackedEnd = true; // Prevent duplicate tracking
            this.trackGameComplete();
        }
        
        // Check if we hit a milestone
        if (this.encouragementMilestones.includes(newReps)) {
            // Use ElevenLabs TTS for consistent voice
            if (window.elevenLabsTTS) {
                window.elevenLabsTTS.speakEncouragement(newReps, this.gameType);
            } else if (window.humeTTS) {
                // Fallback to HUME if ElevenLabs not available
                window.humeTTS.speakEncouragement(newReps, this.gameType);
            }
        }
    }
    
    // Reset reps when starting a new session
    resetSession() {
        this.currentReps = 0;
        // Reset tracking flags for new session
        this.hasStartedTracking = false;
        this.hasTrackedEnd = false;
        this.gameStartTime = null;
        if (window.elevenLabsTTS) {
            window.elevenLabsTTS.resetMilestones(this.gameType);
            window.elevenLabsTTS.startInactivityMonitoring();
        } else if (window.humeTTS) {
            window.humeTTS.resetGame(this.gameType);
        }
    }
    
    async init(game_state, window){
        await this.init_scene(window)

        // Support async init functions
        for (const func of this.on_init) {
            await func(this.scene_data);
        }
        this.game_state = game_state

        // Reset session on init
        this.resetSession();

        // Start inactivity monitoring
        if (window.elevenLabsTTS) {
            window.elevenLabsTTS.startInactivityMonitoring();
        }

        this.animate()
        document.getElementById("camera_canvas").style.scale = this.canvas_scale
    } 

    checkWebGLSupport() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            return !!gl;
        } catch (e) {
            return false;
        }
    }

    async init_scene(window){
        // OPTIMIZATION: Get quality profile
        const quality = getQualityProfile?.() || {
            rendering: {
                pixelRatio: 1,
                antialiasing: true,
                disableShadows: false
            }
        };

        const canvas = document.getElementById("gamecanvas")

        // Check for WebGL support early
        if (!this.checkWebGLSupport()) {
            throw new Error('WebGL is not supported on this device');
        }

        const scene = new THREE.Scene()
        scene.background = null

        const background_scene = new THREE.Scene()
        background_scene.background = null

        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 40)
        camera.position.z = 1
        camera.position.y = 0.2

        let renderer;

        // OPTIMIZATION: Fix WebGPU check - don't throw error if not available
        // Check if WebGPU is available only if we're trying to use it
        if(use_webgpu && !navigator.gpu) {
            console.warn('WebGPU not supported, falling back to WebGL');
            this.use_webgpu = false;
        } else {
            this.use_webgpu = use_webgpu;
        }


        if(this.use_webgpu){
            // Create single WebGPU renderer
            renderer = new THREE_WEBGPU.WebGPURenderer({
                canvas: canvas,
                antialias: quality.rendering.antialiasing,
                alpha: true
            })
        }
        else{
            // Create single WebGL renderer
            renderer = new THREE.WebGLRenderer({
                canvas: canvas,
                antialias: quality.rendering.antialiasing,
                alpha: true,
                powerPreference: 'high-performance',
                failIfMajorPerformanceCaveat: false // Allow rendering even on low-end devices
            })
        }

        if(this.use_webgpu){
            try {
                await renderer.init() // WebGPU requires async initialization
            } catch (error) {
                console.error('WebGPU initialization failed, falling back to WebGL:', error);
                // Fallback to WebGL
                this.use_webgpu = false;
                renderer = new THREE.WebGLRenderer({
                    canvas: canvas,
                    antialias: quality.rendering.antialiasing,
                    alpha: true,
                    powerPreference: 'high-performance',
                    failIfMajorPerformanceCaveat: false
                });
            }
        } else {
        }

        // Adaptive pixel ratio
        const pixelRatio = Math.min(window.devicePixelRatio * quality.rendering.pixelRatio, 2);
        renderer.setPixelRatio(pixelRatio)
        renderer.setClearColor(0x000000, 0)
        renderer.autoClear = true

        // Enable shadows only if quality allows
        if (!quality.rendering.disableShadows) {
            renderer.shadowMap.enabled = true
            renderer.shadowMap.type = THREE.PCFSoftShadowMap
        } else {
            renderer.shadowMap.enabled = false
        }

        renderer.setSize(window.innerWidth, window.innerHeight)

        // OPTIMIZATION: Handle WebGL context loss (critical for iPad/iOS)
        if (!this.use_webgpu) {
            canvas.addEventListener('webglcontextlost', (event) => {
                event.preventDefault();
                console.error('WebGL context lost. Attempting recovery...');
                this.handleContextLoss();
            });

            canvas.addEventListener('webglcontextrestored', () => {
                
                this.handleContextRestore();
            });
        }

        const scene_objs = []

        this.create_lights(scene)
        this.create_lights(background_scene)

        this.scene_data = {
            renderer: renderer,
            scene: scene,
            background_scene: background_scene,
            camera: camera,
            indicators: [],
            objs: scene_objs
        }
    }


    /*
    
        Look here for video plane material setup

    */
/*
    async waitForVideoReady(canvas) {


    }

    create_camera_plane(){
        const video = new THREE.CanvasTexture(document.getElementById("camera_canvas"));

        video.colorSpace = THREE.SRGBColorSpace
        video.minFilter = THREE.LinearFilter
        video.magFilter = THREE.LinearFilter

        // Create a plane geometry and material
        const geometry = new THREE.PlaneGeometry(1, 1); // Match window aspect ratio with height of 1
        const material = new THREE.MeshBasicMaterial({
            map: video,
            transparent: true,
            side: THREE.DoubleSide
        });

        
        // Create the plane mesh
        this.videoplane = new THREE.Mesh(geometry, material);
        this.videoplane.scale.set(window.innerWidth/window.innerHeight, 1, 1)
        this.videoplane.position.z = -2
        this.videoplane.lookAt(this.scene_data.camera.position)
        this.scene_data.background_scene.add(this.videoplane);
        
    }
*/

    // Initialize game name (but don't start tracking yet)
    initializeGameName() {
        

        // Only set gameName if it's not already set explicitly
        if (!this.gameName || this.gameName === 'Unknown Game') {
            // Use gameType if set, otherwise fall back to constructor name
            if (this.gameType && this.gameType !== 'game') {
                this.gameName = this.gameType;
                
            } else if (this.constructor.name && this.constructor.name !== 'PhysicsGame') {
                this.gameName = this.constructor.name.replace('Game', '');
                
            }
        } else {
            
        }

        
        // Don't track start yet - will track when unpaused
        this.hasStartedTracking = false;
    }

    // Analytics tracking methods removed - GameArea.js handles all tracking now
    // trackGameAbandon() and trackGameComplete() moved to GameArea.js
    
    // Legacy method stubs kept for backwards compatibility
    trackGameAbandon(userName, gameName, durationSeconds) {
        
    }

    trackGameComplete() {
        
    }
    
    /* 
     * OLD ANALYTICS CODE REMOVED - Now handled by GameArea.js
     * All game tracking (start/complete/abandon) is done at the GameArea level
     * using the useAnalytics hook and minions branch approach
     */
}